/**
 * **The registration flow's hero photograph: one of two, rolled per step.**
 *
 * Owner decision, 21 September 2026, with the D3 poster mockup
 * (`docs/design/rejestracja-premium-2026-09-21/d3-plakat-dark-losowanie.html`). Every step of
 * sign-up draws a photograph of its own, and every step has TWO of them — so two visitors filling
 * the same form are not looking at the same four pictures, and a trainer who abandons and comes
 * back does not meet a flow they have already read.
 *
 * | step | index 0 | index 1 |
 * |---|---|---|
 * | 1 — the role choice, and the mobile-app pointer behind it | walking in, man | walking in, woman |
 * | 2 — credentials | wrapping his wrists | stretching on a mat |
 * | 3 — confirm the address | setting up a deadlift | under the bar, squatting |
 * | 4 — `/complete-profile` | sitting still on a mat | stretching on a mat |
 *
 * **Fifty-fifty at every step, and the four rolls are independent.** What replaced the previous
 * model — one PERSONA rolled at 70/30 and followed through the flow — is a decision the owner made
 * looking at the mockup: the pane is a poster per step rather than a story about one person, and a
 * weighting nobody can see is a weighting nobody asked for.
 *
 * ── The one rule that is not a coin toss ─────────────────────────────────────────────────────
 * The stretch stands in TWO pairs — it is step 2's second photograph and step 4's second — so a
 * visit that rolled it on step 2 would meet it again three screens later. `photoForStep` answers
 * step 4 with the calm photograph in exactly that case, and the visit therefore never repeats a
 * frame. It is here rather than in the roll so the stored record stays four honest coin tosses:
 * a rule applied at read time can be changed without every tab that stored a roll disagreeing
 * with it.
 *
 * ── This module is PURE, and the randomness arrives as a parameter ───────────────────────────
 * `rollAuthHeroPhotos` takes the generator and the store arrives as a `PreferenceStore`, so the
 * split, the no-repeat rule and the tolerance for an older stored value are all testable without a
 * DOM and without stubbing a global. The wiring lives in `useAuthHeroPhoto`.
 *
 * ── What is stored is the CHOICE, never the URL ──────────────────────────────────────────────
 * The stored record is the four rolls — `{"1":0,"2":1,"3":0,"4":1}` — and the photographs are
 * resolved out of the table below on every read. Storing `photo.src` would be storing a BUILD
 * ARTEFACT: Vite fingerprints every asset, so `work-man-wraps.<hash>.jpg` changes name on the next
 * deploy and a tab that outlived it would paint a 404 into the pane. Anything this build cannot
 * read — including the `{persona, work}` record the previous model wrote — resolves to `null` and
 * is replaced by a fresh roll, which is the ordinary production-discipline reading of anything
 * persisted in a browser.
 *
 * ── The crop is data, and it is per photograph ───────────────────────────────────────────────
 * The frame is portrait at roughly 0.765 (585×765 at 1512×1000) and the sources are 0.46–0.56, so
 * `cover` keeps between 60% and 73% of each image's height and the only question is WHICH. That is
 * a decision made by looking at the photograph — where the face is, where the bar is, whether the
 * feet may leave the frame — so it cannot live in a stylesheet keyed on nothing. Every value below
 * is the mockup's own `PHOTOS` table, which was authored against this same frame; it rides with the
 * photo as `position` and is applied inline (see `ui/HeroPhoto.tsx` for why that is the one inline
 * style the house rules allow here).
 */

import arriveMan from '../assets/auth/arrive-man.jpg';
import arriveWoman from '../assets/auth/arrive-woman.jpg';
import workManSquat from '../assets/auth/work-man-squat.jpg';
import workManWraps from '../assets/auth/work-man-wraps.jpg';
import workWomanCalm from '../assets/auth/work-woman-calm.jpg';
import workWomanDeadlift from '../assets/auth/work-woman-deadlift.jpg';
import workWomanStretch from '../assets/auth/work-woman-stretch.jpg';

import type { PreferenceStore } from './preferenceStore';

/**
 * One photograph and the crop it wants.
 *
 * `src` is whatever the bundler resolved the import to; `position` is a CSS `object-position` value.
 */
export interface AuthHeroPhoto {
  readonly src: string;
  readonly position: string;
}

/**
 * Which beat of the flow a screen stands on.
 *
 * Four, because the flow has four screens with a photograph: the role choice, the credentials, the
 * confirmation and `/complete-profile`. The mobile-app pointer is not a fifth — it stands beside
 * the role choice it was reached from and shares its beat (`pages/sign-up/model/signUpFlow`).
 */
export type AuthHeroStep = 1 | 2 | 3 | 4;

/** Which of a step's two photographs this visit landed on. */
export type AuthHeroChoice = 0 | 1;

/** The four coin tosses, as they are stored and as they are read back. */
export type AuthHeroRoll = Readonly<Record<AuthHeroStep, AuthHeroChoice>>;

/** Every step, in order — so a loop over the flow is the table's own list rather than a copy. */
export const AUTH_HERO_STEPS: readonly AuthHeroStep[] = [1, 2, 3, 4];

/**
 * The whole table: two photographs per step, each with the crop it was chosen at.
 *
 * Read the file header for where the crops come from and for the one pair that overlaps another.
 */
const PAIRS: Readonly<Record<AuthHeroStep, readonly [AuthHeroPhoto, AuthHeroPhoto]>> = {
  1: [
    { src: arriveMan, position: '50% 40%' },
    { src: arriveWoman, position: '52% 42%' },
  ],
  2: [
    { src: workManWraps, position: '58% 44%' },
    { src: workWomanStretch, position: '50% 46%' },
  ],
  3: [
    { src: workWomanDeadlift, position: '50% 48%' },
    { src: workManSquat, position: '50% 46%' },
  ],
  4: [
    { src: workWomanCalm, position: '50% 52%' },
    { src: workWomanStretch, position: '50% 46%' },
  ],
};

/** `movebuddy.` prefixed, as every preference this app stores is. */
const KEY = 'movebuddy.authHeroRoll';

/**
 * **The one entry point**: the four rolls this visit is following, stored so the next screen agrees.
 *
 * Reading, rolling and writing are one decision rather than three, because the interesting part is
 * what happens when the read fails — and a caller that had to compose them could compose them
 * differently on the second screen. Anything unreadable lands on a FRESH roll that is then stored:
 * a store this browser does not have, a value an older build wrote, a key that was never there
 * (the ordinary first arrival), and a social sign-in that never passed through screen one at all.
 *
 * `random` is a parameter for the same reason the store is: so the split can be pinned.
 */
export function resolveAuthHeroRoll(
  store: PreferenceStore | null,
  random: () => number,
): AuthHeroRoll {
  const stored = parseStoredRoll(readStored(store));
  if (stored !== null) return stored;

  const rolled = rollAuthHeroPhotos(random);
  writeStored(store, rolled);
  return rolled;
}

/** Four independent coin tosses, one per step. */
export function rollAuthHeroPhotos(random: () => number): AuthHeroRoll {
  return {
    1: toss(random()),
    2: toss(random()),
    3: toss(random()),
    4: toss(random()),
  };
}

/**
 * The photograph a step draws under this visit's roll.
 *
 * Step 4 falls back to its FIRST photograph when step 2 rolled the stretch, which is the whole of
 * the no-repeat rule — see the file header for why it lives here rather than in the roll.
 */
export function photoForStep(roll: AuthHeroRoll, step: AuthHeroStep): AuthHeroPhoto {
  if (step === 4 && PAIRS[4][roll[4]].src === PAIRS[2][roll[2]].src) return PAIRS[4][0];
  return PAIRS[step][roll[step]];
}

/**
 * A stored value read back, or `null` for every value this build cannot make sense of.
 *
 * `unknown` rather than `string | null` because the shape is exactly what must not be assumed: the
 * argument is whatever came out of a store some other build wrote into, and the only honest type
 * for that is none. The previous model's `{"persona":"man","work":"wraps"}` lands here and is
 * refused like anything else.
 */
function parseStoredRoll(value: unknown): AuthHeroRoll | null {
  const record = asRecord(value);
  if (record === null) return null;

  const [first, second, third, fourth] = AUTH_HERO_STEPS.map((step) => record[String(step)]);
  if (!isChoice(first) || !isChoice(second) || !isChoice(third) || !isChoice(fourth)) return null;

  return { 1: first, 2: second, 3: third, 4: fourth };
}

function isChoice(value: unknown): value is AuthHeroChoice {
  return value === 0 || value === 1;
}

/** Half the number line each way. `Math.random` is `[0, 1)`, so `< 0.5` is an even split. */
function toss(random: number): AuthHeroChoice {
  return random < 0.5 ? 0 : 1;
}

/** A JSON object, from a JSON string or from an object already parsed. Anything else is `null`. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      // Not JSON at all. An older build, a hand-edited value, a half-written string.
      return null;
    }
  }

  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function readStored(store: PreferenceStore | null): string | null {
  if (store === null) return null;
  try {
    return store.getItem(KEY);
  } catch {
    // Reading storage can throw rather than answer null, and a photograph is never worth the screen.
    return null;
  }
}

function writeStored(store: PreferenceStore | null, roll: AuthHeroRoll): void {
  if (store === null) return;

  try {
    store.setItem(KEY, JSON.stringify(roll));
  } catch {
    // A full or refused store loses the roll and nothing else: this visit keeps the photographs it
    // just rolled, and a later screen rolls its own. Two sets of pictures in one flow is a blemish,
    // not a fault, and it is the only outcome available when nothing can be written down.
  }
}
